// src/pages/EventPage.jsx
import { useParams, Link } from "react-router-dom";
import { useEffect, useState, useMemo, useContext, useCallback } from "react";
import { MapContainer, TileLayer, Marker } from "react-leaflet";
import L from "leaflet";

import "../styles/EventPage.css"; // <-- NOWY CSS
import { AuthContext } from "../auth/AuthContext";
import useWatchlist from "../hooks/useWatchlist";
import CreateEventModal from "../pages/CreateEventModal.jsx";
import { useNavigate } from "react-router-dom";
import { getGoogleCalendarUrl } from "../utils/calendarExport.js";
import SimilarEventsPanel from "../components/SimilarEventsPanel";
import ReportEventModal from "../components/ReportEventModal.jsx";
import Navbar from "../components/Navbar.jsx";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const divIconFor = (color = "#888") =>
  L.divIcon({
    className: "ev2-custom-marker",
    html: `<div class="pulse" style="width:20px;height:20px;background:${color};border-radius:50%;border:3px solid white;box-shadow:0 6px 18px rgba(0,0,0,.2)"></div>`,
    iconSize: [20, 20],
    iconAnchor: [10, 10],
  });

export default function EventPage() {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { user, accessToken } = useContext(AuthContext);
  const { toggle, isAuthenticated } = useWatchlist();
  const navigate = useNavigate();

  const [showReportModal, setShowReportModal] = useState(false);

  const isOnline = event?.location_type === "online";
  const eventUrl = event?.ext_event_url || event?.link || null;

  const onUpdateEvent = useCallback(
    (eventId, patch) => {
      setEvent((prev) => (prev && prev.id === eventId ? { ...prev, ...patch } : prev));
    },
    [setEvent]
  );

  const handleToggleWatch = async (eventObj, ev) => {
    ev.stopPropagation();
    const prev = !!eventObj.is_watched;

    // optimistic
    onUpdateEvent(eventObj.id, { is_watched: !prev });

    const res = await toggle(eventObj.id, prev);
    if (!res.ok) {
      onUpdateEvent(eventObj.id, { is_watched: prev });

      if (!isAuthenticated) {
        const el = document.getElementById("authModal");
        if (el && window.bootstrap?.Modal) {
          const modal = window.bootstrap.Modal.getOrCreateInstance(el);
          modal.show();
        }
        // return;
      }
    }
  };

  function getGoogleMapsDirectionsUrl(ev) {
    if (ev?.lat && ev?.lng) {
      return `https://www.google.com/maps/dir/?api=1&destination=${ev.lat},${ev.lng}&travelmode=transit`;
    }
    if (ev?.address) {
      return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
        ev.address
      )}&travelmode=transit`;
    }
    return null;
  }

  useEffect(() => {
    let abort = false;
    setLoading(true);

    const headers = accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined;

    fetch(`${API_BASE}/api/events/${id}/`, { headers })
      .then((res) => {
        if (!res.ok) throw new Error("Nie udało się pobrać wydarzenia");
        return res.json();
      })
      .then((data) => {
        if (!abort) {
          setEvent(data);
          setError(null);
        }
      })
      .catch((err) => {
        if (!abort) setError(err.message || "Wystąpił błąd");
      })
      .finally(() => {
        if (!abort) setLoading(false);
      });

    return () => {
      abort = true;
    };
  }, [id, accessToken]);

  const formatted = useMemo(() => {
    if (!event?.start_datetime) return null;
    const start = new Date(event.start_datetime);

    const dateStr = start.toLocaleDateString("pl-PL", {
      weekday: "long",
      day: "2-digit",
      month: "long",
      year: "numeric",
    });

    const timeStr = start.toLocaleTimeString("pl-PL", {
      hour: "2-digit",
      minute: "2-digit",
    });

    return { dateStr, timeStr };
  }, [event]);

  const priceLabel = useMemo(() => {
    if (!event) return "";
    const { price_type_label, price_from, price_to, currency } = event;

    if (price_from && price_to && price_from !== price_to) {
      return `${price_from}–${price_to} ${currency || "PLN"}`;
    }
    if (price_from && (!price_to || price_to === price_from)) {
      return `Od ${price_from} ${currency || "PLN"}`;
    }
    if (price_type_label && price_type_label !== "Brak informacji") {
      return price_type_label;
    }
    return "Brak informacji";
  }, [event]);

  if (loading) {
    return (
      <main className="ev2">
        <div className="ev2__canvas">
          <div className="ev2__loading">Ładowanie…</div>
        </div>
      </main>
    );
  }

  if (error || !event) {
    return (
      <main className="ev2">
        <div className="ev2__canvas">
          <div className="ev2__error">
            <p>Ups, nie udało się wczytać tego wydarzenia.</p>
            <Link to="/" className="ev2-btn ev2-btn--ghost">
              ← Wróć do listy wydarzeń
            </Link>
          </div>
        </div>
      </main>
    );
  }

  const gmapsUrl = getGoogleMapsDirectionsUrl(event);

  return (
  <main className="ev2">
    {/* tło / bloby */}
    <div className="ev2__bg" aria-hidden="true">
      <div className="ev2__blob ev2__blob--a" />
      <div className="ev2__blob ev2__blob--b" />
      <div className="ev2__blob ev2__blob--c" />
      <div className="ev2__blob ev2__blob--d" />
      <div className="ev2__blob ev2__blob--e" />
      <div className="ev2__veil" />
    </div>

    <Navbar variant="default" />
    
    <div className="ev2__container">
      
      {/* TOP BAR */}
      <header className="ev2-topbar">
        <Link to="/" className="ev2-btn ev2-btn--back" aria-label="Wróć do wydarzeń">
          <span className="ev2-btn__icon">
            <i className="bi bi-arrow-left-short" aria-hidden="true" />
          </span>
          <span>Wróć do wydarzeń</span>
        </Link>

      </header>

      {/* ACTIONS (pod topbarem – na desktop po prawej, na mobile wrap) */}

      {/* GRID LAYOUT */}
      <section className="ev2-layout">
        {/* HERO */}
        <section className="ev3-hero" aria-label="Hero">
          {event.image && (
            <figure className="ev3-hero__figure">
              <img
                className="ev3-hero__img"
                src={event.image}
                alt={event.image_alt || event.name}
                loading="lazy"
              />
            </figure>
          )}

          <div className="ev3-hero__body">
            
            <h1 className="ev3-hero__title">{event.name}</h1>

            <div className="ev3-hero__cats" aria-label="Kategorie">
              {event.category_names?.slice(0, 6)?.map((cat) => (
                <span key={cat} className="ev3-chip">
                  {cat}
                </span>
              ))}
              {event.location_type === "online" && (
                <span className="ev3-chip ev3-chip--online">
                  <i className="bi bi-wifi" aria-hidden="true" />
                  Online
                </span>
              )}
            </div>
          </div>
          
        </section>

        {/* INFO */}
        <div className="ev2-info-cta" aria-label="Akcje wydarzenia">
          {event.created_by !== user?.id && (
            <button
              type="button"
              className="ev2-btn ev2-btn--fav"
              onClick={(ev) => handleToggleWatch(event, ev)}
            >
              <i className={`bi ${event.is_watched ? "bi-heart-fill" : "bi-heart"}`} />
              <span>{event.is_watched ? "Usuń z ulubionych" : "Dodaj do ulubionych"}</span>
            </button>
          )}

          <button
            type="button"
            className="ev2-btn ev2-btn--calendar"
            onClick={(ev) => {
              ev.stopPropagation();
              window.open(getGoogleCalendarUrl(event), "_blank", "noopener,noreferrer");
            }}
          >
            Dodaj do kalendarza
          </button>
        </div>
        <aside className="ev2-info" aria-label="Informacje">
          <div className="ev2-section-title">INFORMACJE</div>

          {event.is_past && (
            <div className="ev2-hero__past">
              <span className="ev2-hero__past-title">Wydarzenie zakończone</span>
              <span className="ev2-hero__past-text">
                To wydarzenie już się odbyło. Sprawdź inne aktualne wydarzenia.
              </span>
            </div>
          )}


          <div className="ev2-info__list">
            <div className="ev2-info__row">
              <div className="ev2-info__icon">
                <i className="bi bi-calendar-event" />
              </div>
              <div className="ev2-info__text">
                <div className="ev2-info__label">data i godzina</div>
                <div className="ev2-info__value">
                  {formatted ? (
                    <>
                      {formatted.dateStr}
                      <br />
                      {formatted.timeStr}
                    </>
                  ) : (
                    "Brak informacji"
                  )}
                </div>
              </div>
            </div>

            {event.venue_name && (
              <div className="ev2-info__row">
                <div className="ev2-info__icon">
                  <i className="bi bi-geo-alt" />
                </div>
                <div className="ev2-info__text">
                  <div className="ev2-info__label">miejsce</div>
                  <div className="ev2-info__value">{event.venue_name}</div>
                </div>
              </div>
            )}

            {/* {event.address && (
              <div className="ev2-info__row">
                <div className="ev2-info__icon">
                  <i className="bi bi-signpost-2" />
                </div>
                <div className="ev2-info__text">
                  <div className="ev2-info__label">adres</div>
                  <div className="ev2-info__value">
                    {event.address}
                    {event.city ? `, ${event.city}` : ""}
                  </div>
                </div>
              </div>
            )} */}

            <div className="ev2-info__row">
              <div className="ev2-info__icon">
                <i className="bi bi-cash-coin" />
              </div>
              <div className="ev2-info__text">
                <div className="ev2-info__label">cena</div>
                <div className="ev2-info__value">{priceLabel}</div>
              </div>
            </div>

            {event.ext_interested_count != null && (
              <div className="ev2-info__row">
                <div className="ev2-info__icon">
                  <i className="bi bi-heart-fill" />
                </div>
                <div className="ev2-info__text">
                  <div className="ev2-info__label">zainteresowanie</div>
                  <div className="ev2-info__value">
                    {event.ext_interested_count.toLocaleString("pl-PL")}
                  </div>
                </div>
              </div>
            )}

            {(event.ext_creator_name || event.created_by_username) && (
              <div
                className="ev2-info__row ev2-info__row--clickable"
                role="button"
                tabIndex={0}
                onClick={() => {
                  if (event.ext_creator_url) {
                    window.open(event.ext_creator_url, "_blank", "noopener,noreferrer");
                  } else {
                    navigate(`/profile/${event.created_by}`);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    if (event.ext_creator_url) {
                      window.open(event.ext_creator_url, "_blank", "noopener,noreferrer");
                    } else {
                      navigate(`/profile/${event.created_by}`);
                    }
                  }
                }}
              >
                <div className="ev2-info__icon">
                  <i className="bi bi-person-badge" />
                </div>
                <div className="ev2-info__text">
                  <div className="ev2-info__label">organizator</div>
                  <div className="ev2-info__value">
                    {event.ext_creator_name || event.created_by_username || "Brak informacji"}
                  </div>
                </div>
              </div>
            )}

            {event.source && (
              <div className="ev2-info__row">
                <div className="ev2-info__icon">
                  <i className="bi bi-globe2" />
                </div>
                <div className="ev2-info__text">
                  <div className="ev2-info__label">źródło</div>
                  <div className="ev2-info__value">{event.source}</div>
                </div>
              </div>
            )}
          </div>

          <div className="ev2-info__footer">
            <button
              type="button"
              className="ev2-linkbtn"
              onClick={() => setShowReportModal(true)}
            >
              Zgłoś naruszenie
            </button>

            {(event.created_by === user?.id || user?.is_superuser) && (
              <button
                type="button"
                className="ev2-linkbtn"
                data-bs-toggle="modal"
                data-bs-target="#editEventModal"
              >
                Edytuj wydarzenie
              </button>
            )}
          </div>
        </aside>

        {/* DESC */}
        <div className="ev2-desc" aria-label="Opis wydarzenia">
          <div className="ev2-section-title">OPIS WYDARZENIA</div>

          <div className="ev2-desc__body">
            {event.description ? (
              <div className="ev2-desc__pre">{event.description}</div>
            ) : (
              <p className="ev2-paragraph ev2-muted">
                Brak opisu. Spróbuj sprawdzić szczegóły u organizatora.
              </p>
            )}
          </div>

          <div className="ev2-desc__actions">
            {event.ext_event_id && (
              <a
                href={`https://www.facebook.com/events/${event.ext_event_id}`}
                target="_blank"
                rel="noreferrer"
                className="ev2-btn ev2-btn--gradient ev2-btn--cta"
              >
                Zobacz wydarzenie u organizatora
              </a>
            )}

            {(event.ext_creator_url || event.created_by_username) && (
              <a
                href={event.ext_creator_url || `/profile/${event.created_by}`}
                target="_blank"
                rel="noreferrer"
                className="ev2-btn ev2-btn--ghost"
              >
                Profil organizatora
              </a>
            )}

            {event.link && (
              <a href={event.link} target="_blank" rel="noreferrer" className="ev2-btn ev2-btn--ghost">
                Strona wydarzenia
              </a>
            )}
          </div>
        </div>

        {/* MAP */}
        <div className="ev2-map" aria-label="Lokalizacja">
  {/* BUTTON NAD MAPĄ */}
  <div className="ev2-map__top">
    {isOnline ? (
      <a
        className={`ev2-btn ev2-btn--gradient ev2-btn--map ${!eventUrl ? "is-disabled" : ""}`}
        href={eventUrl || undefined}
        target={eventUrl ? "_blank" : undefined}
        rel={eventUrl ? "noreferrer" : undefined}
        onClick={(e) => {
          if (!eventUrl) e.preventDefault();
        }}
      >
        <i className="bi bi-box-arrow-up-right" aria-hidden="true" />
        Przejdź do strony wydarzenia
      </a>
    ) : (
      <a
        className={`ev2-btn ev2-btn--gradient ev2-btn--map ${!gmapsUrl ? "is-disabled" : ""}`}
        href={gmapsUrl || undefined}
        target={gmapsUrl ? "_blank" : undefined}
        rel={gmapsUrl ? "noreferrer" : undefined}
        onClick={(e) => {
          if (!gmapsUrl) e.preventDefault();
        }}
      >
        <i className="bi bi-compass" aria-hidden="true" />
        Pokaż trasę w Google Maps
      </a>
    )}
  </div>

  {/* MAPA */}
  <div className="ev2-map__frame">
    {!isOnline && event.lat && event.lng ? (
      <MapContainer
        className="ev2-map__leaflet"
        center={[event.lat, event.lng]}
        zoom={14}
        scrollWheelZoom
      >
        <TileLayer url="https://cartodb-basemaps-a.global.ssl.fastly.net/light_all/{z}/{x}/{y}{r}.png" />
        <Marker position={[event.lat, event.lng]} icon={divIconFor("#f97316")} />
      </MapContainer>
    ) : (
      <div className="ev2-map__placeholder">
        {isOnline ? "Wydarzenie online" : "Brak danych lokalizacji"}
      </div>
    )}

    {/* OVERLAY NA DOLE MAPY */}
    <div className="ev2-map__overlay" aria-label="Szczegóły lokalizacji">
      {isOnline ? (
        <div className="ev2-map__overlayRow">
          <div className="ev2-map__overlayIcon">
            <i className="bi bi-wifi" aria-hidden="true" />
          </div>
          <div className="ev2-map__overlayText">
            <div className="ev2-map__overlayLabel">wydarzenie</div>
            <div className="ev2-map__overlayValue">Online</div>
          </div>
        </div>
      ) : (
        <>
          {event.venue_name && (
            <div className="ev2-map__overlayRow">
              <div className="ev2-map__overlayIcon">
                <i className="bi bi-geo-alt" aria-hidden="true" />
              </div>
              <div className="ev2-map__overlayText">
                <div className="ev2-map__overlayLabel">miejsce</div>
                <div className="ev2-map__overlayValue">{event.venue_name}</div>
              </div>
            </div>
          )}

          {(event.address || event.city) && (
            <div className="ev2-map__overlayRow">
              <div className="ev2-map__overlayIcon">
                <i className="bi bi-signpost-2" aria-hidden="true" />
              </div>
              <div className="ev2-map__overlayText">
                <div className="ev2-map__overlayLabel">adres</div>
                <div className="ev2-map__overlayValue">
                  {event.address || "Brak dokładnego adresu"}
                  {event.city ? `, ${event.city}` : ""}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  </div>
</div>
      </section>
    </div>

    {/* MODALE / panele dodatkowe */}
    <CreateEventModal
      mode="edit"
      eventId={event.id}
      initialData={event}
      modalId="editEventModal"
      onSuccess={() => {
        setTimeout(() => window.location.reload(), 1200);
      }}
    />

    <ReportEventModal
      show={showReportModal}
      onClose={() => setShowReportModal(false)}
      eventId={event.id}
      isAuthenticated={isAuthenticated}
      authToken={accessToken}
    />

    <SimilarEventsPanel event={event} />
  </main>
);
}
